import React, { Fragment } from "react";
import styles from "../../../styles/AdminConsole.module.css";
import VisitSubTable from "./VisitSubTable";

/**
 * QualityTable
 *
 * The scrollable master table. Each order row has a Fragment containing
 * the order row + its VisitSubTable. VisitSubTable is memoised so only
 * the expanded/changed order re-renders when visits update.
 *
 * Props:
 *   scrollRef          React ref — from useDragScroll()
 *   onMouseDown        from useDragScroll()
 *   onMouseMove        from useDragScroll()
 *   onMouseUp          from useDragScroll()
 *   paginatedOrders    Order[]
 *   visitsByOrder      { [orderId]: Visit[] }
 *   expandedOrderId    string | null
 *   canWrite           bool
 *   isAdmin            bool
 *   deleteConfirm      string | null
 *   currentPage        number
 *   ITEMS_PER_PAGE     number
 *
 *   onToggleExpand     (orderId) => void
 *   onOpenEditOrder    (order) => void
 *   onDeleteOrder      (order) => void
 *   onOpenAddVisit     (order) => void
 *   onOpenEditVisit    (orderId, visit) => void
 *   onDeleteVisit        (orderId, visit) => void
 *   onToggleCleared      (orderId, visit) => void
 *   onToggleFlagged      (orderId, visit) => void
 *   onCancelDelete       () => void
 *   onCancelDeleteRequest        (orderId, visit) => void  — QC member cancels their own pending visit delete request
 *   onCancelOrderDeleteRequest   (order) => void            — QC member cancels their own pending order delete request
 *   getOrderStats        (orderId) => { visitCount, open, totalPaid, isFullyCleared }
 */
const QualityTable = React.memo(function QualityTable({
  scrollRef,
  onMouseDown,
  onMouseMove,
  onMouseUp,
  paginatedOrders,
  visitsByOrder,
  expandedOrderId,
  canWrite,
  isAdmin,
  deleteConfirm,
  currentPage,
  ITEMS_PER_PAGE,
  onToggleExpand,
  onOpenEditOrder,
  onDeleteOrder,
  onOpenAddVisit,
  onOpenEditVisit,
  onDeleteVisit,
  onToggleCleared,
  onToggleFlagged,
  onCancelDelete,
  onCancelDeleteRequest,
  onCancelOrderDeleteRequest,
  getOrderStats,
}) {
  const masterColCount = 9 + (canWrite ? 3 : 0);

  return (
    <div
      ref={scrollRef}
      className={styles.tableScroll}
      style={{
        overflowX: "scroll",
        scrollbarWidth: "none",
        msOverflowStyle: "none",
        cursor: "grab",
        userSelect: "none",
      }}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseUp}
    >
      <table className={styles.table}>
        <thead>
          <tr className={styles.tableHeadRow}>
            <th className={styles.th}>#</th>
            <th className={styles.th}>Order ID</th>
            <th className={styles.th}>Supplier</th>
            <th className={styles.th}>Country</th>
            <th className={styles.th}>City</th>
            <th className={styles.th}>Visits</th>
            <th className={styles.th}>Open</th>
            <th className={styles.th}>Paid (₹)</th>
            {canWrite && <th className={styles.th}>Edit</th>}
            {canWrite && <th className={styles.th}>Delete</th>}
            {canWrite && <th className={styles.th}>+</th>}
            <th className={styles.th}></th>
          </tr>
        </thead>
        <tbody>
          {paginatedOrders.length === 0 && (
            <tr>
              <td
                className={styles.td}
                colSpan={masterColCount}
                style={{ textAlign: "center", opacity: 0.5 }}
              >
                No orders found.
              </td>
            </tr>
          )}

          {paginatedOrders.map((order, idx) => {
            const { visitCount, open, totalPaid } = getOrderStats(order.id);
            const visits = visitsByOrder[order.id] || [];
            const isOpen = expandedOrderId === order.id;

            return (
              <Fragment key={order.id}>
                {/* ── Master row ── */}
                <tr
                  className={`${styles.tableRow} ${isOpen ? styles.masterRowExpanded : ""}`}
                >
                  <td className={styles.td} style={{ opacity: 0.5 }}>
                    {(currentPage - 1) * ITEMS_PER_PAGE + idx + 1}
                  </td>

                  <td
                    className={styles.td}
                    style={{ fontWeight: 600, whiteSpace: "nowrap" }}
                  >
                    {order.orderId || order.id}
                  </td>

                  <td className={styles.td}>{order.supplier || "—"}</td>

                  <td className={styles.td}>
                    <span
                      className={`${styles.categoryBadge} ${styles.cat_woven}`}
                    >
                      {order.country || "—"}
                    </span>
                  </td>

                  <td className={styles.td}>{order.city || "—"}</td>

                  <td className={styles.td}>
                    {visitCount > 0 ? (
                      <span className={styles.rollupValue}>{visitCount}</span>
                    ) : (
                      <span className={styles.rollupMuted}>0</span>
                    )}
                  </td>

                  <td className={styles.td}>
                    {open > 0 ? (
                      <span style={{ color: "#e08080", fontWeight: 700 }}>
                        {open}
                      </span>
                    ) : (
                      <span className={styles.rollupMuted}>0</span>
                    )}
                  </td>

                  <td className={styles.td} style={{ whiteSpace: "nowrap" }}>
                    {totalPaid > 0 ? (
                      <span className={styles.rollupValue}>
                        ₹{totalPaid.toLocaleString("en-IN")}
                      </span>
                    ) : (
                      <span className={styles.rollupMuted}>—</span>
                    )}
                  </td>

                  {canWrite && (
                    <td className={styles.td}>
                      <button
                        className={styles.editBtn}
                        onClick={() => onOpenEditOrder(order)}
                        title="Edit order"
                      >
                        ✎
                      </button>
                    </td>
                  )}

                  {canWrite && (
                    <td className={styles.td}>
                      {order.deleteRequested && !isAdmin ? (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          <span
                            style={{
                              fontSize: 9,
                              opacity: 0.55,
                              whiteSpace: "nowrap",
                            }}
                          >
                            Pending review
                          </span>
                          <button
                            className={styles.btn}
                            style={{ padding: "4px 10px", fontSize: 9 }}
                            onClick={() => onCancelOrderDeleteRequest(order)}
                            title="Cancel delete request"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : deleteConfirm === `order:${order.id}` ? (
                        <div style={{ display: "flex", gap: 4 }}>
                          <button
                            className={`${styles.btn} ${styles.btnPrimary}`}
                            style={{
                              padding: "4px 10px",
                              fontSize: 9,
                              marginTop: 0,
                              background: "#c84040",
                              borderColor: "#c84040",
                            }}
                            onClick={() => onDeleteOrder(order)}
                          >
                            Confirm
                          </button>
                          <button
                            className={styles.btn}
                            style={{ padding: "4px 10px", fontSize: 9 }}
                            onClick={onCancelDelete}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          className={styles.editBtn}
                          style={{ color: "#e08080" }}
                          onClick={() => onDeleteOrder(order)}
                          title={
                            isAdmin
                              ? "Delete order & all its visits"
                              : "Request deletion of this order & all its visits"
                          }
                        >
                          ✕
                        </button>
                      )}
                    </td>
                  )}

                  {canWrite && (
                    <td className={styles.td}>
                      <button
                        className={styles.addVisitBtn}
                        onClick={() => onOpenAddVisit(order)}
                        title="Log a visit for this order"
                      >
                        +
                      </button>
                    </td>
                  )}

                  <td className={styles.td}>
                    <button
                      className={`${styles.expandToggleBtn} ${isOpen ? styles.expandToggleBtnOpen : ""}`}
                      onClick={() => onToggleExpand(order.id)}
                      title={isOpen ? "Collapse visits" : "Show visits"}
                    >
                      ▸
                    </button>
                  </td>
                </tr>

                {/* ── Visit sub-table — memoised per order ── */}
                <VisitSubTable
                  orderId={order.id}
                  order={order}
                  visits={visits}
                  isOpen={isOpen}
                  canWrite={canWrite}
                  isAdmin={isAdmin}
                  masterColCount={masterColCount}
                  deleteConfirm={deleteConfirm}
                  onOpenAddVisit={onOpenAddVisit}
                  onOpenEditVisit={onOpenEditVisit}
                  onDeleteVisit={onDeleteVisit}
                  onToggleCleared={onToggleCleared}
                  onToggleFlagged={onToggleFlagged}
                  onCancelDelete={onCancelDelete}
                  onCancelDeleteRequest={onCancelDeleteRequest}
                />
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
});

export default QualityTable;
